import React, { useEffect, useState } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import { makeStyles } from '@material-ui/core/styles';
import { parseISO, isToday, isYesterday, subDays, isWithinInterval } from 'date-fns';
import {
  Grid,
  Paper,
  Typography
} from '@material-ui/core';
import { listActivityUserApps } from '../graphql/queries';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3)
  },
  metricCard: {
    padding: theme.spacing(2),
    textAlign: 'center'
  },
  chartCard: {
    padding: theme.spacing(2),
    margin: theme.spacing(2),
    height: 600
  },
  chartTitle: {
    marginBottom: theme.spacing(1)
  }
}));

const ViewUserActivity = () => {
  const classes = useStyles();
  const [userActivity, setUserActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const foreverStart = new Date('2025-03-25');
  const [dateRangeStart, setDateRangeStart] = useState(new Date('2025-03-25'));
  const [dateRangeEnd, setDateRangeEnd] = useState(new Date());

  useEffect(() => {
    fetchAllUserActivity();
  }, []);

  const fetchAllUserActivity = async () => {
    let allData = [];
    let nextToken = null;
    try {
      do {
        const response = await API.graphql(graphqlOperation(listActivityUserApps, { nextToken, limit: 1000 }));
        const data = response.data.listActivityUserApps;
        allData = [...allData, ...data.items];
        nextToken = data.nextToken;
      } while (nextToken);
      setUserActivity(allData);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // Data for active user cards (always after foreverStart)
  const baseData = userActivity.filter(item => parseISO(item.createdAt) >= foreverStart);
  const todayActiveUsers = getActiveUsersCount(baseData, isToday);
  const yesterdayActiveUsers = getActiveUsersCount(baseData, isYesterday);
  const last7DaysActiveUsers = getActiveUsersCount(baseData, (date) =>
    isWithinInterval(date, { start: subDays(new Date(), 6), end: new Date() })
  );

  function getActiveUsersCount(data, predicate) {
    const users = new Set();
    data.forEach(item => {
      if (item.createdAt && item.User && item.User.name) {
        const createdAt = parseISO(item.createdAt);
        if (predicate(createdAt)) {
          users.add(item.User.name);
        }
      }
    });
    return users.size;
  }

  // Data for charts based on selectable date range
  const chartData = userActivity.filter(item => {
    const created = parseISO(item.createdAt);
    return created >= dateRangeStart && created <= dateRangeEnd;
  });

  const topUsersMap = {};
  chartData.forEach(item => {
    if (item.User && item.User.name) {
      const name = item.User.name;
      topUsersMap[name] = (topUsersMap[name] || 0) + 1;
    } else {
      topUsersMap["Unknown"] = (topUsersMap["Unknown"] || 0) + 1;
    }
  });
  const topUsersArray = Object.entries(topUsersMap)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  const topPagesMap = {};
  chartData.forEach(item => {
    const page = item.page || "Unknown";
    topPagesMap[page] = (topPagesMap[page] || 0) + 1;
  });
  const topPagesArray = Object.entries(topPagesMap)
    .map(([page, count]) => ({ page, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  const topSectionsMap = {};
  chartData.forEach(item => {
    const section = item.section || "Unknown";
    topSectionsMap[section] = (topSectionsMap[section] || 0) + 1;
  });
  const topSectionsArray = Object.entries(topSectionsMap)
    .map(([section, count]) => ({ section, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  const handleStartDateChange = (date) => {
    setDateRangeStart(date);
  };

  const handleEndDateChange = (date) => {
    setDateRangeEnd(date);
  };

  return (
    <div className={classes.container}>
      <Typography variant="h4" gutterBottom>App Usage Dashboard</Typography>
      {loading ? (
        <Typography variant="body1">Loading data...</Typography>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper className={classes.metricCard}>
                <Typography variant="h6">Active Users Today</Typography>
                <Typography variant="h3">{todayActiveUsers}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper className={classes.metricCard}>
                <Typography variant="h6">Active Users Yesterday</Typography>
                <Typography variant="h3">{yesterdayActiveUsers}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper className={classes.metricCard}>
                <Typography variant="h6">Active Users Last 7 Days</Typography>
                <Typography variant="h3">{last7DaysActiveUsers}</Typography>
              </Paper>
            </Grid>
          </Grid>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <KeyboardDatePicker
                  disableToolbar
                  variant="inline"
                  format="yyyy-MM-dd"
                  margin="normal"
                  label="Start Date"
                  value={dateRangeStart}
                  onChange={handleStartDateChange}
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <KeyboardDatePicker
                  disableToolbar
                  variant="inline"
                  format="yyyy-MM-dd"
                  margin="normal"
                  label="End Date"
                  value={dateRangeEnd}
                  onChange={handleEndDateChange}
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                />
              </Grid>
            </Grid>
          </MuiPickersUtilsProvider>
          <Typography variant="subtitle1" gutterBottom>
            Showing chart data from {dateRangeStart.toISOString().split('T')[0]} to {dateRangeEnd.toISOString().split('T')[0]}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper className={classes.chartCard}>
                <Typography variant="h6" className={classes.chartTitle}>Top Users by Actions</Typography>
                <ResponsiveContainer width="100%" height="85%">
                  <BarChart data={topUsersArray} margin={{ top: 20, right: 20, left: 20, bottom: 120 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ angle: 45, textAnchor: 'start' }} interval={0} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" name="Actions" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper className={classes.chartCard}>
                <Typography variant="h6" className={classes.chartTitle}>Top Pages Viewed</Typography>
                <ResponsiveContainer width="100%" height="85%">
                  <BarChart data={topPagesArray} margin={{ top: 20, right: 20, left: 20, bottom: 120 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="page" tick={{ angle: 45, textAnchor: 'start' }} interval={0} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" name="Views" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper className={classes.chartCard}>
                <Typography variant="h6" className={classes.chartTitle}>Top Sections Viewed</Typography>
                <ResponsiveContainer width="100%" height="85%">
                  <BarChart data={topSectionsArray} margin={{ top: 20, right: 20, left: 20, bottom: 120 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="section" tick={{ angle: 45, textAnchor: 'start' }} interval={0} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" name="Views" fill="#ffc658" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </div>
  );
};

export default ViewUserActivity;
